import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  AlertCircle, HelpCircle, RefreshCw, Mail,
  CreditCard, ShieldAlert, Banknote,
  Laptop, Network, FileText, Database, Terminal, Globe
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface TroubleshootingContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const TroubleshootingContent: React.FC<TroubleshootingContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "common-issues") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Common Issues</h1>
          <p className="text-gray-500 mt-2">
            Solutions to frequently encountered problems on the DGK Platform.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 2, 2023</span>
            <Badge variant="outline" className="ml-2">Support</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Before You Begin</AlertTitle>
            <AlertDescription>
              Most technical issues can be resolved by clearing your browser cache or trying a different browser. 
              For account-specific issues, ensure you're logged in with the correct credentials.
            </AlertDescription>
          </Alert>

          <h2 id="login-registration">Login & Registration Issues</h2>
          
          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="cant-login">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                  Can't Log In
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    If you're having trouble logging into your account, try these solutions:
                  </p>
                  <ol className="space-y-2 list-decimal pl-5">
                    <li>Verify you're using the correct email address and password combination</li>
                    <li>Check your internet connection and try again</li>
                    <li>Clear your browser cookies and cache</li>
                    <li>Try a different browser or device</li>
                    <li>Use the "Forgot Password" link to reset your password</li>
                    <li>Ensure your account hasn't been suspended (check your email for notifications)</li>
                  </ol>
                  <div className="bg-blue-50 p-3 rounded-md mt-3">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> If you've recently changed your email address, you might need to use your previous email to log in until the change is fully processed.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="account-locked">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <ShieldAlert className="h-5 w-5 mr-2 text-amber-500" />
                  Account Locked or Suspended
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    Your account may be locked or suspended for several reasons:
                  </p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li>Multiple failed login attempts (security precaution)</li>
                    <li>Violation of our Terms of Service or Community Guidelines</li>
                    <li>Payment issues or overdue invoices</li>
                    <li>Suspicious activity detected on your account</li>
                    <li>Account verification required</li>
                  </ul>
                  <div className="bg-amber-50 p-3 rounded-md mt-3">
                    <p className="text-sm text-amber-700">
                      <strong>Important:</strong> If your account has been suspended, you should have received an email explaining the reason and the steps to restore access. Check your email (including spam folder) for details.
                    </p>
                  </div>
                  <Button className="mt-2" size="sm">
                    Contact Support
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="verification-issues">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-blue-500" />
                  Email Verification Issues
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  <p>
                    If you're having trouble with email verification:
                  </p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li>Check your spam folder for the verification email</li>
                    <li>Add noreply@dgkplatform.com to your email contacts</li>
                    <li>Request a new verification email from your account settings</li>
                    <li>Make sure you're clicking the link within 24 hours (verification links expire)</li>
                    <li>Try a different email address if problems persist</li>
                  </ul>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm">
                      Resend Verification
                    </Button>
                    <Button variant="outline" size="sm">
                      Change Email
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <h2 id="payment-issues">Payment & Billing Issues</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <CreditCard className="h-5 w-5 text-red-500 mr-2" />
                  <h3 className="font-medium">Payment Method Declined</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  If your payment method is being declined, verify the following:
                </p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  <li>Card has not expired</li>
                  <li>Sufficient funds available</li>
                  <li>Billing address matches your card details</li>
                  <li>Bank is not blocking the transaction</li>
                </ul>
                <div className="mt-4">
                  <Button size="sm" variant="outline" className="w-full">
                    Update Payment Method
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Banknote className="h-5 w-5 text-green-500 mr-2" />
                  <h3 className="font-medium">Missing Payment or Payout</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  If you're missing a payment or expected payout:
                </p>
                <ul className="text-sm space-y-1 list-disc pl-5">
                  <li>Check pending transactions in your wallet</li>
                  <li>Verify milestone completion status</li>
                  <li>Confirm bank account details are correct</li>
                  <li>Allow 3-5 business days for processing</li>
                </ul>
                <div className="mt-4">
                  <Button size="sm" variant="outline" className="w-full">
                    View Transaction History
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="technical-issues">Technical Issues</h2>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-6">
            <h3 className="text-base font-medium mb-3">Platform Performance Issues</h3>
            <div className="space-y-4">
              <div>
                <p className="font-medium text-sm mb-1">Site Loading Slowly</p>
                <p className="text-sm text-gray-600">
                  Try clearing your browser cache, disabling extensions, or using a different browser. Check your internet connection speed.
                </p>
              </div>
              <div>
                <p className="font-medium text-sm mb-1">Features Not Working</p>
                <p className="text-sm text-gray-600">
                  Ensure your browser is up to date. Try disabling ad blockers or privacy extensions which might interfere with site functionality.
                </p>
              </div>
              <div>
                <p className="font-medium text-sm mb-1">File Upload Errors</p>
                <p className="text-sm text-gray-600">
                  Check that your file meets the size and format requirements. Try compressing large files or using a different file format.
                </p>
              </div>
              <div className="mt-3">
                <Button size="sm" className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Check System Status
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["common-issues"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("common-issues", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("common-issues", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("common-issues") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("common-issues")}
            >
              {bookmarkedArticles.includes("common-issues") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  } else if (activePage === "error-messages") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Error Messages</h1>
          <p className="text-gray-500 mt-2">
            Common error messages on the DGK Platform and how to resolve them.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 15, 2023</span>
            <Badge variant="outline" className="ml-2">Technical</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Code Reference</AlertTitle>
            <AlertDescription>
              This page provides explanations and solutions for common error codes you might encounter while using the platform.
            </AlertDescription>
          </Alert>

          <Tabs defaultValue="ui-errors" className="w-full my-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ui-errors">UI Errors</TabsTrigger>
              <TabsTrigger value="api-errors">API Errors</TabsTrigger>
              <TabsTrigger value="auth-errors">Authentication Errors</TabsTrigger>
            </TabsList>
            
            <TabsContent value="ui-errors" className="space-y-4 mt-4">
              <h3 className="text-lg font-semibold">UI Error Messages</h3>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="ui-error-1">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <Laptop className="h-5 w-5 mr-2 text-red-500" />
                      "Failed to load component" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error occurs when a UI component fails to load properly.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Network connectivity issues</li>
                        <li>Outdated browser cache</li>
                        <li>Client-side JavaScript errors</li>
                        <li>Incompatible browser version</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-decimal pl-5">
                        <li>Try refreshing the page</li>
                        <li>Clear your browser cache and cookies</li>
                        <li>Try using a different web browser</li>
                        <li>Check if your browser is up to date</li>
                        <li>Disable browser extensions temporarily</li>
                      </ol>
                      <div className="bg-blue-50 p-3 rounded-md mt-3">
                        <p className="text-sm text-blue-700">
                          <strong>Developer Note:</strong> If you're a developer, check the browser console for more specific error messages that might help identify the issue.
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="ui-error-2">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-amber-500" />
                      "Form Submission Failed" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error appears when a form submission fails to process correctly.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Invalid form data or missing required fields</li>
                        <li>Server-side validation errors</li>
                        <li>Session timeout</li>
                        <li>Network connection issues</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-decimal pl-5">
                        <li>Check all form fields for errors or validation messages</li>
                        <li>Verify that all required fields are filled out</li>
                        <li>Try logging out and logging back in</li>
                        <li>Check your internet connection</li>
                        <li>If using a public Wi-Fi, switch to a more secure connection</li>
                      </ol>
                      <div className="mt-4 p-3 bg-amber-50 rounded-md">
                        <p className="text-sm text-amber-700">
                          <strong>Note:</strong> If you're submitting sensitive information or financial data, make sure you're on a secure connection (https) before attempting again.
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="ui-error-3">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <Globe className="h-5 w-5 mr-2 text-blue-500" />
                      "Content Unavailable" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error occurs when requested content cannot be displayed or accessed.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Content has been removed or archived</li>
                        <li>Permission issues - you may not have access to the requested content</li>
                        <li>The content URL has changed</li>
                        <li>Regional restrictions or compliance issues</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-disc pl-5">
                        <li>Verify that you have the necessary permissions to access the content</li>
                        <li>Check if the content has been moved to another location</li>
                        <li>Contact the content owner to request access</li>
                        <li>Try accessing the content from a different account if possible</li>
                      </ol>
                      <Button size="sm" className="mt-3">
                        Contact Support
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>
            
            <TabsContent value="api-errors" className="space-y-4 mt-4">
              <h3 className="text-lg font-semibold">API Error Codes</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Solution</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">API-001</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Authentication Failed</td>
                      <td className="px-6 py-4 text-sm text-gray-500">The API request is missing valid authentication credentials.</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Ensure you're using a valid API key and proper authentication headers.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">API-002</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Rate Limit Exceeded</td>
                      <td className="px-6 py-4 text-sm text-gray-500">You've exceeded the allowed number of API requests for your plan.</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Wait before making more requests or upgrade your plan for higher limits.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">API-003</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Invalid Parameters</td>
                      <td className="px-6 py-4 text-sm text-gray-500">The request contains invalid or malformed parameters.</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Check the API documentation for required format and parameters.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">API-004</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Resource Not Found</td>
                      <td className="px-6 py-4 text-sm text-gray-500">The requested resource does not exist.</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Verify the resource ID or path is correct and exists in your account.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">API-005</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Server Error</td>
                      <td className="px-6 py-4 text-sm text-gray-500">An unexpected error occurred on the server.</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Contact support with the error details and timestamp of the request.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg mt-4">
                <h4 className="text-sm font-medium text-blue-700 mb-2">Developer Tip</h4>
                <p className="text-sm text-blue-700">
                  When troubleshooting API errors, always check the complete response body as it often contains 
                  detailed error information beyond the status code.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="auth-errors" className="space-y-4 mt-4">
              <h3 className="text-lg font-semibold">Authentication Errors</h3>
              
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Security Notice</AlertTitle>
                <AlertDescription>
                  If you encounter repeated authentication errors despite using correct credentials, 
                  your account may be experiencing a security incident. Consider changing your password immediately.
                </AlertDescription>
              </Alert>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="auth-error-1">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <ShieldAlert className="h-5 w-5 mr-2 text-red-500" />
                      "Invalid Credentials" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error appears when your username/email and password combination is incorrect.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Incorrect password</li>
                        <li>Mistyped email address</li>
                        <li>Using an old password after a password reset</li>
                        <li>Caps Lock is enabled</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-decimal pl-5">
                        <li>Double-check your email/username for typos</li>
                        <li>Verify that Caps Lock is turned off</li>
                        <li>Use the "Forgot Password" option to reset your password</li>
                        <li>Clear browser cookies and try again</li>
                      </ol>
                      <div className="flex gap-3 mt-3">
                        <Button variant="outline" size="sm">
                          Forgot Password
                        </Button>
                        <Button size="sm">
                          Contact Support
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="auth-error-2">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <Terminal className="h-5 w-5 mr-2 text-amber-500" />
                      "Session Expired" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error occurs when your login session has timed out due to inactivity or security policies.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Extended period of inactivity</li>
                        <li>Session timeout settings on the platform</li>
                        <li>Browser cookie limitations or settings</li>
                        <li>Logging in from a new device (security feature)</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-decimal pl-5">
                        <li>Log in again with your credentials</li>
                        <li>Check the "Remember Me" option if you're on a trusted device</li>
                        <li>Adjust your browser cookie settings to accept session cookies</li>
                        <li>For security reasons, sessions automatically expire after 24 hours of inactivity</li>
                      </ol>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="auth-error-3">
                  <AccordionTrigger className="text-base font-medium">
                    <div className="flex items-center">
                      <Database className="h-5 w-5 mr-2 text-blue-500" />
                      "Account Not Found" Error
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      <p>This error appears when trying to log in with an email that is not registered in our system.</p>
                      <h4 className="font-medium">Possible causes:</h4>
                      <ul className="space-y-2 list-disc pl-5">
                        <li>Using an email address that hasn't been registered</li>
                        <li>Account has been deleted or deactivated</li>
                        <li>Attempting to log in with a different authentication method than originally used</li>
                        <li>Typo in email address</li>
                      </ul>
                      <h4 className="font-medium">Solutions:</h4>
                      <ol className="space-y-2 list-decimal pl-5">
                        <li>Verify the email address you're using</li>
                        <li>Check if you registered with a different email address</li>
                        <li>If you used social login (Google, Facebook, etc.), try that method instead</li>
                        <li>Create a new account if you're sure you haven't registered before</li>
                      </ol>
                      <div className="mt-4 flex gap-3">
                        <Button variant="outline" size="sm">
                          Sign Up
                        </Button>
                        <Button size="sm">
                          Contact Support
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["error-messages"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("error-messages", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("error-messages", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("error-messages") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("error-messages")}
            >
              {bookmarkedArticles.includes("error-messages") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  } else if (activePage === "account-issues") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Account Issues</h1>
          <p className="text-gray-500 mt-2">
            Troubleshooting common account-related problems and solutions.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 10, 2023</span>
            <Badge variant="outline" className="ml-2">Account</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <Alert className="mb-6 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <AlertTitle className="text-amber-700">Account Security Notice</AlertTitle>
            <AlertDescription className="text-amber-700">
              Keep your account secure by using a strong password, enabling two-factor authentication, 
              and never sharing your login credentials with others.
            </AlertDescription>
          </Alert>

          <h2 id="account-settings">Account Settings Issues</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <ShieldAlert className="h-5 w-5 text-blue-500 mr-2" />
                  <h3 className="font-medium">Can't Update Profile</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  If you're unable to update your profile information, check the following:
                </p>
                <ul className="text-sm space-y-2 list-disc pl-5 text-gray-600">
                  <li>Verify that all required fields are filled out</li>
                  <li>Check that image uploads meet size and format requirements</li>
                  <li>Ensure you have a stable internet connection</li>
                  <li>Try using a different browser or clearing your cache</li>
                  <li>Some fields may have restrictions (e.g., username can only be changed once every 30 days)</li>
                </ul>
                <div className="mt-4">
                  <Button size="sm" variant="outline" className="w-full">
                    Visit Profile Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <Mail className="h-5 w-5 text-purple-500 mr-2" />
                  <h3 className="font-medium">Email Notifications Not Arriving</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  If you're not receiving email notifications from the platform:
                </p>
                <ul className="text-sm space-y-2 list-disc pl-5 text-gray-600">
                  <li>Check your spam or junk folders</li>
                  <li>Add our domain to your email whitelist</li>
                  <li>Verify your email address is correct in account settings</li>
                  <li>Check that notifications are enabled in your preferences</li>
                  <li>Try updating your email address to a different provider</li>
                </ul>
                <div className="mt-4">
                  <Button size="sm" variant="outline" className="w-full">
                    Manage Notification Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="account-access">Account Access Problems</h2>

          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="two-factor-issues">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                  Two-Factor Authentication (2FA) Issues
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <p className="text-gray-700">
                    Two-factor authentication adds an extra layer of security but can sometimes cause login difficulties.
                  </p>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Common 2FA Problems</h4>
                    <ul className="space-y-3">
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-medium">1</div>
                        <div>
                          <p className="font-medium text-gray-800">Lost access to authentication device</p>
                          <p className="text-sm text-gray-600">If you've lost your phone or can't access your authenticator app, use your backup codes to log in. These were provided when you first set up 2FA.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-500 font-medium">2</div>
                        <div>
                          <p className="font-medium text-gray-800">Incorrect time on authentication device</p>
                          <p className="text-sm text-gray-600">TOTP codes are time-based. If your device's time is not synced correctly, authentication will fail. Update your device time settings.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-medium">3</div>
                        <div>
                          <p className="font-medium text-gray-800">Backup codes not working</p>
                          <p className="text-sm text-gray-600">Ensure you're entering the full code correctly. Each backup code can only be used once. If all codes are used or lost, contact support.</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-700 mb-2">Recovery Options</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      If you're unable to access your account through normal 2FA methods:
                    </p>
                    <ol className="space-y-2 list-decimal pl-5 text-sm text-blue-700">
                      <li>Use your backup codes (recommended)</li>
                      <li>Complete the account recovery process with your email</li>
                      <li>Contact support with verification of your identity</li>
                    </ol>
                    <div className="mt-4 flex gap-3">
                      <Button size="sm" variant="outline">
                        Start Recovery
                      </Button>
                      <Button size="sm">
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="password-reset">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <RefreshCw className="h-5 w-5 mr-2 text-blue-500" />
                  Password Reset Problems
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <p className="text-gray-700">
                    If you're experiencing issues with resetting your password, follow these troubleshooting steps:
                  </p>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Password Reset Issues</h4>
                    <ul className="space-y-4">
                      <li>
                        <p className="font-medium text-gray-800">Not receiving password reset emails</p>
                        <ul className="mt-1 space-y-1 text-sm text-gray-600 list-disc pl-5">
                          <li>Check your spam/junk folders</li>
                          <li>Add noreply@dgkplatform.com to your contacts</li>
                          <li>Try a different email address if possible</li>
                          <li>Verify you're using the correct email associated with your account</li>
                        </ul>
                      </li>
                      <li>
                        <p className="font-medium text-gray-800">Reset link expired or invalid</p>
                        <ul className="mt-1 space-y-1 text-sm text-gray-600 list-disc pl-5">
                          <li>Password reset links expire after 30 minutes for security</li>
                          <li>Request a new password reset email</li>
                          <li>Make sure you're clicking the most recent link sent</li>
                        </ul>
                      </li>
                      <li>
                        <p className="font-medium text-gray-800">New password not accepted</p>
                        <ul className="mt-1 space-y-1 text-sm text-gray-600 list-disc pl-5">
                          <li>Ensure your new password meets all requirements (minimum 8 characters, mix of letters, numbers, and special characters)</li>
                          <li>Don't reuse a recently used password</li>
                          <li>Try a different browser or clear your cache</li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-4">
                    <Button className="w-full">Request Password Reset Again</Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="account-locked">
              <AccordionTrigger className="text-base font-medium">
                <div className="flex items-center">
                  <ShieldAlert className="h-5 w-5 mr-2 text-red-500" />
                  Account Locked or Restricted
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Security Alert</AlertTitle>
                    <AlertDescription>
                      Account restrictions are usually implemented to protect your account from unauthorized access or due to policy violations.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Common Reasons for Account Restrictions</h4>
                    <ul className="space-y-3">
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-medium">1</div>
                        <div>
                          <p className="font-medium text-gray-800">Multiple failed login attempts</p>
                          <p className="text-sm text-gray-600">After several incorrect password attempts, accounts are temporarily locked for 30 minutes to prevent brute force attacks.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-medium">2</div>
                        <div>
                          <p className="font-medium text-gray-800">Suspicious activity detected</p>
                          <p className="text-sm text-gray-600">Logins from unusual locations or devices may trigger security measures. Verify your identity to restore access.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-medium">3</div>
                        <div>
                          <p className="font-medium text-gray-800">Terms of Service violations</p>
                          <p className="text-sm text-gray-600">Accounts found in violation of our platform's terms may be restricted. Review the email sent to your registered address for details.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-medium">4</div>
                        <div>
                          <p className="font-medium text-gray-800">Payment issues</p>
                          <p className="text-sm text-gray-600">Overdue invoices or payment failures may result in account restrictions. Update your payment method to resolve.</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-4 flex flex-col gap-3">
                    <Button variant="outline">
                      Verify Identity
                    </Button>
                    <Button>
                      Contact Support Team
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["account-issues"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("account-issues", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("account-issues", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("account-issues") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("account-issues")}
            >
              {bookmarkedArticles.includes("account-issues") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
